import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import LaunchIcon from '@mui/icons-material/Launch'
import { formatNormLabel } from '../../utils/normLabels'
import PaginationComp from '../../components/common/pagination'
import {
  fetchSecurityCheckFamilyByCategory,
  getSecurityISO,
} from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { writeSecurityNav } from '../../utils/securityNav'
import {
  selectSpecificationLoading,
  selectSpecifications,
} from '../../redux/specification/specificationSelectors'
import i18n from '../../i18n'
import { formatTitle } from '../../utils/formatTitle'
import { splitFamilyLabel } from '../../utils/familyLabel'
import { SecurityCheckFamily } from '../../types/securityCheckFamilysTypes'
import {
  ListPageShell,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { onClickableTableRow } from '../../utils/tableRowClick'
import '../../components/common/ListPageTable.css'

import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { formatDecimal } from '../../utils/formatNumbers'
interface NormListProps {
  normToMap?: string
  navigateTo?: string
}

export default function NormList({ normToMap, navigateTo }: NormListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedNorm: routeNorm } = useParams<{ selectedNorm?: string }>()
  const specsLoading = useSelector(selectSpecificationLoading)
  const normBase = useSelector(selectSpecifications)[0]?.NormBase || ''

  const selectedNorm = normToMap || routeNorm || normBase || ''

  useEffect(() => {
    if (selectedNorm) writeSecurityNav({ norm: selectedNorm })
  }, [selectedNorm])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')

  const dispatch: AppDispatch = useAppDispatch()
  const { SecurityCheckFamilyResponse, loading } = useSelector(
    (state: RootState) => state.securitycheckFamily
  )
  const showLoader = useTableLoadingGate(loading, [
    selectedNorm,
    page,
    pageSize,
  ])

  useEffect(() => {
    // Prefer route/map norm; otherwise wait for NormBase so we never race ISO vs NIS2.
    if (!normToMap && !routeNorm && specsLoading) return

    if (!selectedNorm) {
      dispatch(
        getSecurityISO({
          page,
          pageSize,
          language: i18n.language.toLocaleUpperCase(),
        })
      )
      return
    }
    const action = fetchSecurityCheckFamilyByCategory({
      category: selectedNorm,
      page,
      pageSize,
      language: i18n.language.toLocaleUpperCase(),
    })
    if (action) dispatch(action)
  }, [
    dispatch,
    selectedNorm,
    page,
    pageSize,
    i18n.language,
    specsLoading,
    normToMap,
    routeNorm,
  ])

  const families = SecurityCheckFamilyResponse?.securityCheckFamilies || []

  const filteredFamilies = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return families
    return families.filter((f: SecurityCheckFamily) => {
      const label = (f.LabelSCF || '').toLowerCase()
      const source = (f.Source || '').toLowerCase()
      return label.includes(q) || source.includes(q)
    })
  }, [families, search])

  const normLabel =
    t(`norms.${selectedNorm}.title`, {
      defaultValue: formatNormLabel(selectedNorm),
    }) || formatNormLabel(selectedNorm)

  const detailPath = (id: string) =>
    navigateTo ? `${navigateTo}/${id}` : `/security-controls/${id}`

  const openFamily = (familyId: string, label?: string) => {
    writeSecurityNav({
      norm: selectedNorm,
      familyId,
      familyLabel: label || undefined,
    })
    navigate(detailPath(familyId), {
      state: {
        familyId,
        familyLabel: label,
        norm: selectedNorm,
      },
    })
  }

  return (
    <ListPageShell
      eyebrow={t('securityControls')}
      title={t('iso_security_controls_families_list', {
        norm: formatNormLabel(selectedNorm),
      })}
      titleTourId="security-families-page-header"
      subtitle={t('listPages.securityFamiliesSub', {
        norm: normLabel,
      })}
      search={search}
      onSearchChange={(value) => {
        setSearch(value)
        setPage(1)
      }}
      searchPlaceholder={t('filterByName')}
      countLabel={t('listPages.familiesCount', {
        count: search.trim()
          ? filteredFamilies.length
          : (SecurityCheckFamilyResponse?.total ?? filteredFamilies.length),
      })}
      footer={
        SecurityCheckFamilyResponse?.totalPages &&
        SecurityCheckFamilyResponse.totalPages > 0 &&
        !search.trim() ? (
          <PaginationComp
            page={page}
            pageSize={pageSize}
            onChange={(nextPage, newSize) => {
              setPage(nextPage)
              setPageSize(newSize)
            }}
            totalItems={SecurityCheckFamilyResponse.totalPages}
          />
        ) : undefined
      }
    >
      <div className="incyia-table-wrap">
        {showLoader ? (
          <TableLoadingState />
        ) : filteredFamilies.length === 0 ? (
          <TableEmptyState />
        ) : (
          <table className="incyia-table incyia-table--families">
            <thead>
              <tr>
                <th>{t('Label')}</th>
                <th>{t('Source')}</th>
                <th>{t('listPages.score')}</th>
                <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredFamilies.map((family: SecurityCheckFamily) => {
                const score =
                  family.SCFScore != null &&
                  !Number.isNaN(Number(family.SCFScore))
                    ? formatDecimal(Number(family.SCFScore))
                    : '—'
                const parsed = splitFamilyLabel(
                  family.LabelSCF || '',
                  family.Source || ''
                )
                const label = parsed.full
                  ? formatTitle(parsed.full)
                  : family.LabelSCF
                    ? formatTitle(family.LabelSCF)
                    : undefined

                return (
                  <tr key={family._id} onClick={onClickableTableRow}>
                    <td className="incyia-col-pinned">
                      <button
                        type="button"
                        className="incyia-family-label"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                        }}
                        onClick={() => openFamily(family._id as string, label)}
                      >
                        <span className="incyia-family-code">{parsed.code}</span>
                        {parsed.full ? (
                          <span
                            className="incyia-family-name"
                            title={formatTitle(parsed.full)}
                          >
                            {formatTitle(parsed.full)}
                          </span>
                        ) : null}
                      </button>
                    </td>
                    <td className="incyia-muted">
                      <ListPageDescription value={parsed.source} />
                    </td>
                    <td>
                      <span className="incyia-score">{score}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="incyia-row-actions">
                        <button
                          type="button"
                          title={t('listPages.openAction')}
                          onClick={() =>
                            openFamily(family._id as string, label)
                          }
                          style={listPageIconBtnStyle}
                        >
                          <LaunchIcon sx={{ fontSize: 15 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </ListPageShell>
  )
}
